
import { Navbar } from '@/components/Navbar';
import { Helmet } from 'react-helmet-async';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { HardHat, Shield, Truck, Users, AlertTriangle, FileText } from 'lucide-react';

const ConstructionPEO = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-white to-orange-50">
      <Helmet>
        <title>PEO Services for Construction Companies & Contractors | The PEO App</title>
        <meta name="description" content="Specialized PEO services for construction companies, contractors, and construction trades. Workers' comp, safety compliance, and construction-specific HR solutions." />
        <link rel="canonical" href="https://thepeoapp.com/industries/construction" />
        <meta name="keywords" content="construction PEO, contractor PEO, PEO for construction companies, construction workers comp, construction safety compliance, construction payroll services" />
        
        <meta property="og:title" content="PEO Services for Construction Companies & Contractors" />
        <meta property="og:description" content="Specialized PEO services for construction industry with workers' comp, safety programs, and trade-specific expertise." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://thepeoapp.com/industries/construction" />
      </Helmet>
      
      <Navbar />
      <main className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <HardHat className="h-16 w-16 text-orange-600 mx-auto mb-6" />
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              PEO Services for Construction Companies
            </h1>
            <p className="text-xl text-gray-700 leading-relaxed max-w-3xl mx-auto">
              Professional Employer Organization services built specifically for construction contractors, 
              builders, and construction trades. Expert workers' comp, safety compliance, and construction HR.
            </p>
          </div>

          <div className="bg-white p-8 rounded-xl shadow-lg mb-16">
            <h2 className="text-3xl font-bold mb-6">Construction Industry Challenges</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <AlertTriangle className="h-6 w-6 text-red-500 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">High Workers' Comp Costs</h3>
                    <p className="text-gray-700">Construction has some of the highest workers' compensation rates due to injury risk</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Shield className="h-6 w-6 text-red-500 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Safety Compliance</h3>
                    <p className="text-gray-700">OSHA regulations, safety training, and accident prevention requirements</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Users className="h-6 w-6 text-red-500 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Seasonal Workforce</h3>
                    <p className="text-gray-700">Managing fluctuating employee counts and project-based hiring</p>
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <FileText className="h-6 w-6 text-red-500 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Prevailing Wage Compliance</h3>
                    <p className="text-gray-700">Complex wage requirements for government and union projects</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Truck className="h-6 w-6 text-red-500 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Multi-State Operations</h3>
                    <p className="text-gray-700">Working across state lines with different regulations and requirements</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <HardHat className="h-6 w-6 text-red-500 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Skilled Labor Shortage</h3>
                    <p className="text-gray-700">Difficulty finding and retaining qualified tradespeople</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mb-16">
            <h2 className="text-3xl font-bold mb-8 text-center">Construction-Specialized PEO Solutions</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-orange-50 p-6 rounded-xl">
                <Shield className="h-12 w-12 text-orange-600 mb-4" />
                <h3 className="text-xl font-semibold mb-3">Workers' Comp Expertise</h3>
                <p className="text-gray-700">
                  Specialized workers' compensation programs with construction-focused safety initiatives 
                  and claims management to reduce your mod rates.
                </p>
              </div>
              
              <div className="bg-orange-50 p-6 rounded-xl">
                <AlertTriangle className="h-12 w-12 text-orange-600 mb-4" />
                <h3 className="text-xl font-semibold mb-3">Safety Programs</h3>
                <p className="text-gray-700">
                  OSHA compliance support, safety training programs, toolbox talks, 
                  and accident prevention specifically for construction trades.
                </p>
              </div>
              
              <div className="bg-orange-50 p-6 rounded-xl">
                <FileText className="h-12 w-12 text-orange-600 mb-4" />
                <h3 className="text-xl font-semibold mb-3">Prevailing Wage Support</h3>
                <p className="text-gray-700">
                  Expert handling of Davis-Bacon and state prevailing wage requirements 
                  for government and public works projects.
                </p>
              </div>
            </div>
          </div>

          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-6">Find Construction-Specialized PEOs</h2>
            <p className="text-lg text-gray-700 mb-8">
              Connect with PEO providers who understand construction industry challenges 
              and have proven experience managing construction workforces.
            </p>
            <Link to="/application">
              <Button className="bg-[#00a86b] hover:bg-[#008f5a] text-white text-lg px-8 py-6 rounded-full">
                Get Construction PEO Quotes
              </Button>
            </Link>
          </div>

          <div className="bg-gray-50 p-8 rounded-xl">
            <h2 className="text-2xl font-bold mb-6">Construction Trades We Serve</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-white rounded-lg">
                <h3 className="font-semibold">General Contractors</h3>
                <p className="text-sm text-gray-600">Commercial, residential, industrial construction</p>
              </div>
              <div className="text-center p-4 bg-white rounded-lg">
                <h3 className="font-semibold">Specialty Trades</h3>
                <p className="text-sm text-gray-600">Electrical, plumbing, HVAC, roofing</p>
              </div>
              <div className="text-center p-4 bg-white rounded-lg">
                <h3 className="font-semibold">Heavy Construction</h3>
                <p className="text-sm text-gray-600">Road work, utilities, infrastructure</p>
              </div>
              <div className="text-center p-4 bg-white rounded-lg">
                <h3 className="font-semibold">Subcontractors</h3>
                <p className="text-sm text-gray-600">Concrete, framing, drywall, flooring</p>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ConstructionPEO;
